/**
 * CII (Carbon Intensity Indicator) Grade Color Mapper
 * Based on IMO (International Maritime Organization) Regulations
 *
 * Grades:
 * A - Major superior performance (Green)
 * B - Minor superior performance (Light Green)
 * C - Moderate performance (Yellow)
 * D - Minor inferior performance (Orange)
 * E - Inferior performance (Red)
 */
 
export const CII_GRADE_COLORS = {
  A: {
    color: '#22c55e',      // Green
    bgColor: '#f0fdf4',    // Light green background
    label: 'Excellent',
    description: 'Major superior performance'
  },
  B: {
    color: '#84cc16',      // Lime green
    bgColor: '#f7fee7',    // Light lime background
    label: 'Good',
    description: 'Minor superior performance'
  },
  C: {
    color: '#eab308',      // Yellow
    bgColor: '#fefce8',    // Light yellow background
    label: 'Moderate',
    description: 'Moderate performance'
  },
  D: {
    color: '#f97316',      // Orange
    bgColor: '#fff7ed',    // Light orange background
    label: 'Poor',
    description: 'Minor inferior performance'
  },
  E: {
    color: '#ef4444',      // Red
    bgColor: '#fef2f2',    // Light red background
    label: 'Very Poor',
    description: 'Inferior performance'
  }
};
 
/**
 * Get color configuration for a CII grade
 * @param {string} grade - The CII grade (A, B, C, D, or E)
 * @returns {Object} Color configuration object
 */
export const getCIIGradeColor = (grade) => {
  const normalizedGrade = grade?.toUpperCase().trim();
 
  if (CII_GRADE_COLORS[normalizedGrade]) {
    return CII_GRADE_COLORS[normalizedGrade];
  }
 
  // Default to moderate if grade is invalid or missing
  return {
    color: '#64748b',
    bgColor: '#f8fafc',
    label: 'Unknown',
    description: 'Grade not available'
  };
};
 
/**
 * Get just the text color for a CII grade
 * @param {string} grade - The CII grade
 * @returns {string} Hex color code
 */
export const getCIIGradeTextColor = (grade) => {
  return getCIIGradeColor(grade).color;
};
 
/**
 * Get just the background color for a CII grade
 * @param {string} grade - The CII grade
 * @returns {string} Hex color code
 */
export const getCIIGradeBgColor = (grade) => {
  return getCIIGradeColor(grade).bgColor;
};
 
/**
 * Check if a grade is compliant (A, B, or C)
 * @param {string} grade - The CII grade
 * @returns {boolean} True if compliant
 */
export const isCIIGradeCompliant = (grade) => {
  const normalizedGrade = grade?.toUpperCase().trim();
  return ['A', 'B', 'C'].includes(normalizedGrade);
};
 
/**
 * Check if a grade requires corrective action (D or E)
 * @param {string} grade - The CII grade
 * @returns {boolean} True if corrective action needed
 */
export const requiresCorrectiveAction = (grade) => {
  const normalizedGrade = grade?.toUpperCase().trim();
  return ['D', 'E'].includes(normalizedGrade);
};
 
export default {
  CII_GRADE_COLORS,
  getCIIGradeColor,
  getCIIGradeTextColor,
  getCIIGradeBgColor,
  isCIIGradeCompliant,
  requiresCorrectiveAction
};
 
 